import React from 'react';
import SupplyDepartmentIcon from './SupplyDepartmentIcon';
import LegalDepartmentIcon from './LegalDepartmentIcon';
import DefaultDepartmentIcon from './DefaultDepartmentIcon';
import SepProcessSupervisorIcon from './SepProcessSupervisorIcon';
import SecurityDepartmentIcon from './SecurityDepartmentIcon';
import ThirdPartySecurityIcon from './ThirdPartySecurityIcon';
import SolutionArchitectIcon from './SolutionArchitectIcon';
import EaDepartmentIcon from './EaDepartmentIcon';
import OcmcsIcon from './OcmcsIcon';
import TechnologySteeringBoardIcon from './TechnologySteeringBoardIcon';
import ResponsibleAiAssessmentTeamIcon from './ResponsibleAiAssessmentTeamIcon';
import TaskListIcon from './TaskListIcon';
import AttachmentsIcon from './AttachmentsIcon';
import CommentsIcon from './CommentsIcon';
import ActivityIcon from './ActivityIcon';

// ---------------------------------------------------------------------------
// Shared props for every department icon component
// ---------------------------------------------------------------------------
export interface DepartmentIconProps {
  color?: string;
  height?: number | string;
  width?: number | string;
  opacity?: number;
  style?: React.CSSProperties;
  className?: string;
  'aria-label'?: string;
}

// ---------------------------------------------------------------------------
// Map: backend Department.name  →  icon component
//
// Add a new entry here whenever a new department icon component is created.
// Keys must match the exact string stored in the Departments table.
// ---------------------------------------------------------------------------
export const DEPARTMENT_ICON_MAP: Record<
  string,
  React.FC<DepartmentIconProps>
> = {
  Supply: SupplyDepartmentIcon,
  Legal: LegalDepartmentIcon,
  'SEP Process Supervisor': SepProcessSupervisorIcon,
  Security: SecurityDepartmentIcon,
  'Third Party Security': ThirdPartySecurityIcon,
  'Solution Architect': SolutionArchitectIcon,
  EA: EaDepartmentIcon,
  OCMCS: OcmcsIcon,
  'Technology Steering Board': TechnologySteeringBoardIcon,
  'Responsible AI Assessment Team': ResponsibleAiAssessmentTeamIcon,
  'Task List': TaskListIcon,
  'Attachments': AttachmentsIcon,
  'Comments': CommentsIcon,
  Activity: ActivityIcon,

  // Placeholder entries – replace with the imported component once the
  // TSX icon component is added to src/frontend/assets/svg/
  //
  // EA:                      EaDepartmentIcon,
  // Security:                SecurityDepartmentIcon,
  // 'Third Party Security':  ThirdPartySecurityDepartmentIcon,
  // 'Nuclear Cyber Security':NuclearCyberSecurityDepartmentIcon,
  // 'Portfolio Owner':       PortfolioOwnerDepartmentIcon,
  // 'Solution Architect':    SolutionArchitectDepartmentIcon,
  // 'SEP Process Supervisor':SepProcessSupervisorDepartmentIcon,
};

// ---------------------------------------------------------------------------
// DepartmentIconRenderer
//
// Generic component used anywhere in the app.
// Pass the backend department name and optional style overrides.
// Renders nothing when no icon is mapped for the given name.
// ---------------------------------------------------------------------------
export interface DepartmentIconRendererProps extends DepartmentIconProps {
  /** Exact value of Department.name from the backend */
  departmentName: string;
}

const DepartmentIconRenderer: React.FC<DepartmentIconRendererProps> = ({
  departmentName,
  color,
  height,
  width,
  opacity,
  style,
  className,
  'aria-label': ariaLabel,
}) => {
  const IconComponent = DEPARTMENT_ICON_MAP[departmentName] ?? DefaultDepartmentIcon;

  return (
    <IconComponent
      color={color}
      height={height}
      width={width}
      opacity={opacity}
      style={style}
      className={className}
      aria-label={ariaLabel ?? departmentName}
    />
  );
};

export default DepartmentIconRenderer;
